import { useState, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { Text, theme, styles as baseStyles } from '@socrait/react';
import { QueryStatus } from '@tanstack/react-query';

import { ErrorBox } from '~/components/ErrorBox';
import { Loading } from '~/components/Loading';
import { useViewClassroomMomentsFeature, useViewWeeklyInsightsFeature } from '~/featureFlags';
import { MomentsResponse, ReviewActionItemsResponse, WeeklyInsightsResponse } from '~/services/review';
import { ActionItem, Period } from '~/services/types';

import { ActionAccordionItem } from './ui/actionItem/ActionAccordionItem';
import { DataLogActionItem } from './ui/actionItem/DataLogActionItem';
import { ClassroomMoments } from './ui/ClassroomMoments';
import { DeleteReviewModal } from './ui/DeleteReviewModal';
import { ReviewKebabMenu } from './ui/ReviewKebabMenu';
import { WeeklyInsights } from './ui/WeeklyInsights';

interface ReviewActionItemsProps {
  period: Period;
  date: Date;
  hasPraiseOrWarningItems: boolean;
  actionItemsData?: ReviewActionItemsResponse | null;
  actionItemsQueryStatus: QueryStatus;
  actionItemsError?: Error | null;
  weeklyInsightsData?: WeeklyInsightsResponse | null;
  weeklyInsightsQueryStatus: QueryStatus;
  momentsData?: MomentsResponse | null;
  momentsQueryStatus: QueryStatus;
}

const isNew = ({ status }: ActionItem) => status === 'new';
const isClassSummary = ({ type }: ActionItem) => type === 'class_summary';

const filterActionItems = (items: ActionItem[]): ActionItem[] => {
  let summaryItem: ActionItem | undefined;
  const otherItems: ActionItem[] = [];

  for (const item of items) {
    if (!isNew(item)) {
      continue;
    }

    if (isClassSummary(item)) {
      if (!summaryItem) {
        summaryItem = item;
      }
      continue;
    }

    otherItems.push(item);
  }

  return [summaryItem, ...otherItems].filter(Boolean) as ActionItem[];
};

export const ReviewActionItems = ({
  period,
  date,
  hasPraiseOrWarningItems,
  actionItemsData,
  actionItemsQueryStatus,
  actionItemsError,
  weeklyInsightsData,
  weeklyInsightsQueryStatus,
  momentsData,
  momentsQueryStatus,
}: ReviewActionItemsProps) => {
  const [isDeleteReviewModalOpen, setIsDeleteReviewModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const viewWeeklyInsightsFeature = useViewWeeklyInsightsFeature();
  const viewClassroomMomentsFeature = useViewClassroomMomentsFeature();

  const closeMenu = () => setIsMenuOpen(false);

  const hasData = actionItemsData?.actionItems?.length || hasPraiseOrWarningItems;

  const reviewActionItemsContent = useMemo(() => {
    const processedActionItems = actionItemsData?.actionItems ? filterActionItems(actionItemsData.actionItems) : [];

    if (!hasData) {
      return (
        <View style={styles.noDataContainer}>
          <Text style={styles.noDataTitle}>There's nothing here yet!</Text>
          <Text style={styles.noDataText}>Waiting for data from your class</Text>
        </View>
      );
    } else {
      return (
        <>
          {processedActionItems.map((item) => (
            <ActionAccordionItem
              key={item.id}
              actionId={item.id}
              title={item.title}
              type={item.type}
              studentVoiceName={item.studentVoiceName}
              message={item.message}
              date={date}
              periodId={period.id}
            />
          ))}
          {hasPraiseOrWarningItems && <DataLogActionItem date={date} title={'Data Log'} periodId={period.id} />}
        </>
      );
    }
  }, [hasData, hasPraiseOrWarningItems, date, period.id, actionItemsData?.actionItems]);

  if (actionItemsQueryStatus === 'pending') {
    return <Loading backgroundColor={theme.colors.transparent} />;
  }

  if (actionItemsError) {
    return <ErrorBox text="Could not load action items. Please try refreshing the page." />;
  }

  return (
    <View style={styles.container}>
      {viewWeeklyInsightsFeature && <WeeklyInsights data={weeklyInsightsData} status={weeklyInsightsQueryStatus} />}
      {viewClassroomMomentsFeature && <ClassroomMoments data={momentsData} status={momentsQueryStatus} />}
      <View style={styles.actionAccordionItems}>
        <View style={styles.actionTitleContainer}>
          <Text style={styles.actionTitle}>Suggested Actions</Text>
          {hasData && (
            <ReviewKebabMenu
              isMenuOpen={isMenuOpen}
              toggleMenu={() => setIsMenuOpen((prev) => !prev)}
              closeMenu={closeMenu}
              setIsDeleteReviewModalOpen={setIsDeleteReviewModalOpen}
            />
          )}
        </View>
        {reviewActionItemsContent}
        <DeleteReviewModal
          period={period}
          date={date}
          isOpen={isDeleteReviewModalOpen}
          onClose={() => setIsDeleteReviewModalOpen(false)}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    gap: 10,
  },
  actionTitleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    height: 40,
  },
  actionTitle: {
    ...baseStyles.title,
    paddingHorizontal: theme.spacing.medium,
    paddingVertical: theme.spacing.xsmall,
    fontSize: theme.fontSizes.small,
  },
  actionAccordionItems: {
    flex: 1,
    flexDirection: 'column',
    gap: 10,
  },
  noDataContainer: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.large,
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.xlarge,
  },
  noDataTitle: {
    ...baseStyles.title,
    color: theme.colors.darkGray,
    lineHeight: theme.lineHeights.regular * 2,
    textAlign: 'center',
  },
  noDataText: {
    color: theme.colors.darkGray,
    lineHeight: theme.lineHeights.regular,
    textAlign: 'center',
  },
});
